import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { supabase, invokeFunction } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Upload, Presentation, GraduationCap, Users, Plus, Trash2, Target, Award, ExternalLink, Sparkles, BarChart3, Download, FileText, FileStack, Network, Settings, RefreshCw } from "lucide-react";
import { Session } from "@supabase/supabase-js";
import { ScrollArea } from "@/components/ui/scroll-area";
import UploadMaterialDialog from "@/components/UploadMaterialDialog";
import MaterialsList from "@/components/MaterialsList";
import SkillTreeView from "@/components/SkillTreeView";
import GenerateSlidesDialog from "@/components/GenerateSlidesDialog";
import SlideDecksView from "@/components/SlideDecksView";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import ShareCourseDialog from "@/components/ShareCourseDialog";
import AddieOutlineView from "@/components/AddieOutlineView";
import CreationOutlineView from "@/components/CreationOutlineView";
import SyllabusPrintView from "@/components/SyllabusPrintView";

import CourseOutlineSidebar from "@/components/CourseOutlineSidebar";
import { useCourseContext } from "@/contexts/CourseContext";
import { useUserType } from "@/hooks/useUserType";

interface Course {
  id: string;
  user_id: string;
  title: string;
  description: string | null;
  learner_profile: any;
  objectives: any;
  status: string;
  is_public: boolean;
  created_at: string;
  workspace_id: string | null;
}

interface Workspace {
  id: string;
  name: string;
  icon: string | null;
}

interface CourseObjective {
  id: string;
  title: string;
  description: string | null;
  order_index: number;
}

const MAX_COURSE_OBJECTIVES = 60;

/** Split pasted text into objective titles (one per line); strips bullets and numbering. */
function splitPastedObjectiveLines(raw: string): string[] {
  const lines = raw.split(/\r?\n/);
  const out: string[] = [];
  for (let line of lines) {
    line = line.trim();
    if (!line) continue;
    line = line
      .replace(/^[\s•·▪▸]+/u, "")
      .replace(/^[\-\*]\s+/, "")
      .replace(/^\d+[\.\)]\s+/, "")
      .trim();
    if (line) out.push(line);
  }
  return out;
}

const Course = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [session, setSession] = useState<Session | null>(null);
  const [course, setCourse] = useState<Course | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [addingSourcesWithAI, setAddingSourcesWithAI] = useState(false);
  const [slidesDialogOpen, setSlidesDialogOpen] = useState(false);
  const [slideDecks, setSlideDecks] = useState<any[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editedCourse, setEditedCourse] = useState<Partial<Course>>({});
  const [courseObjectives, setCourseObjectives] = useState<CourseObjective[]>([]);
  const [editedObjectives, setEditedObjectives] = useState<CourseObjective[]>([]);
  const [objectivesPasteDraft, setObjectivesPasteDraft] = useState("");
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [showManageMembers, setShowManageMembers] = useState(false);
  const [certifications, setCertifications] = useState<any[]>([]);
  const [loadingCertifications, setLoadingCertifications] = useState(false);
  const [activeSection, setActiveSection] = useState<"outline" | "materials" | "skilltree" | "slides" | "details">("outline");
  const { toast } = useToast();
  const { setCourseContext, clearCourseContext } = useCourseContext();
  const { userType } = useUserType(session?.user?.id);
  const [addieOutline, setAddieOutline] = useState<any>(null);
  const [freeTierAddieOutline, setFreeTierAddieOutline] = useState<any>(null);
  const [generatingOutline, setGeneratingOutline] = useState(false);
  const lastPushedOutlineKeyRef = useRef<string>("");

  // Set course context when course loads
  useEffect(() => {
    if (course) {
      setCourseContext({
        courseId: course.id,
        courseName: course.title,
        nodeId: null,
        nodeName: null,
        pageType: "course",
      });
    }
    
    return () => {
      clearCourseContext();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course]);

  // When returning from a node lesson (Materials outline) or opening course with openMaterials, show Materials tab
  useEffect(() => {
    const state = location.state as { fromMaterials?: boolean; openMaterials?: boolean } | null;
    if (state?.openMaterials || state?.fromMaterials) {
      setActiveSection("materials");
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (!session) {
        navigate("/auth");
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (!session) {
        navigate("/auth");
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const fetchAddieOutline = async () => {
    if (!id) return;
    const { data } = await supabase
      .from("addie_outlines")
      .select("*")
      .eq("project_id", id)
      .maybeSingle();
    setAddieOutline(data ?? null);
    // Outline is source of truth: push to skill tree so chapters/nodes stay in sync
    const outline = data?.creation_outline;
    const hasSections = outline && typeof outline === "object" && (
      (Array.isArray((outline as { sections?: unknown[] }).sections) && (outline as { sections: unknown[] }).sections.length > 0) ||
      (Array.isArray((outline as { modules?: unknown[] }).modules) && (outline as { modules: unknown[] }).modules.length > 0)
    );
    if (hasSections) {
      const sec = (outline as { sections?: unknown[] }).sections ?? (outline as { modules?: unknown[] }).modules;
      const outlineKey = JSON.stringify(sec);
      if (lastPushedOutlineKeyRef.current !== outlineKey) {
        lastPushedOutlineKeyRef.current = outlineKey;
        invokeFunction("save-addie-outline", {
          body: { project_id: id, creation_outline: outline },
        }).catch((err) => {
          lastPushedOutlineKeyRef.current = "";
          console.warn("[Course] save-addie-outline failed:", err);
        });
      }
    }
  };

  useEffect(() => {
    if (session && id) {
      fetchCourse();
      fetchSlideDecks();
      fetchCourseObjectives();
      fetchAddieOutline();
    }
  }, [session, id]);

  // Refetch outline when assistant (or elsewhere) updates content so UI and skill tree stay in sync
  useEffect(() => {
    const handler = () => {
      if (id) fetchAddieOutline();
    };
    window.addEventListener("course-content-updated", handler);
    return () => window.removeEventListener("course-content-updated", handler);
  }, [id]);

  // When landing from course creation, refetch outline so outline and skill tree appear right away
  useEffect(() => {
    const fromCreation = (location.state as { fromCreation?: boolean })?.fromCreation;
    if (!fromCreation || !id) return;
    fetchAddieOutline(); // immediate refetch so left panel and Skill Tree show the scaffold
    const t = setTimeout(() => {
      fetchAddieOutline();
      navigate(location.pathname, { replace: true, state: {} });
    }, 600);
    return () => clearTimeout(t);
  }, [id, location.pathname, location.state]);

  // Fetch ADDIE outline for free-tier (other_one_course) limited view
  useEffect(() => {
    if (userType !== "other_one_course" || !id) return;
    (async () => {
      const { data } = await supabase
        .from("addie_outlines")
        .select("*")
        .eq("project_id", id)
        .maybeSingle();
      setFreeTierAddieOutline(data ?? null);
    })();
  }, [userType, id]);

  useEffect(() => {
    if (session && id && course) {
      const loadCertifications = async () => {
        if (!id) {
          setLoadingCertifications(false);
          return;
        }
        
        setLoadingCertifications(true);
        try {
          // Fetch skill tree which contains target_certifications
          const { data: skillTree, error } = await supabase
            .from("skill_trees")
            .select("id, target_certifications")
            .eq("course_id", id)
            .maybeSingle();

          if (error && error.code !== "PGRST116") {
            console.error("Error fetching certifications:", error);
            setCertifications([]);
          } else if (skillTree?.target_certifications && Array.isArray(skillTree.target_certifications) && skillTree.target_certifications.length > 0) {
            setCertifications(skillTree.target_certifications);
          } else if (skillTree?.id) {
            // If no target_certifications in tree, check nodes for certification_domains
            const { data: nodes } = await supabase
              .from("skill_nodes")
              .select("certification_domains")
              .eq("skill_tree_id", skillTree.id);
            
            if (nodes && nodes.length > 0) {
              const certNames = new Set<string>();
              nodes.forEach((node: any) => {
                if (node?.certification_domains && Array.isArray(node.certification_domains)) {
                  node.certification_domains.forEach((cd: any) => {
                    if (cd?.certification) {
                      certNames.add(cd.certification);
                    }
                  });
                }
              });
              
              if (certNames.size > 0) {
                setCertifications(Array.from(certNames).map(name => ({
                  name,
                  issuer: "Unknown",
                  domains: []
                })));
              } else {
                setCertifications([]);
              }
            } else {
              setCertifications([]);
            }
          } else {
            setCertifications([]);
          }
        } catch (error) {
          console.error("Error fetching certifications:", error);
          setCertifications([]);
        } finally {
          setLoadingCertifications(false);
        }
      };
      
      loadCertifications();
    }
  }, [session, id, course]);

  const fetchCourseObjectives = async () => {
    if (!id) return;
    const { data, error } = await supabase
      .from("course_objectives")
      .select("*")
      .eq("course_id", id)
      .order("order_index", { ascending: true });

    if (!error && data) {
      setCourseObjectives(data);
      setEditedObjectives(data);
    }
  };

  const fetchCourse = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("learning_projects")
      .select("*")
      .eq("id", id)
      .single();

    if (error) {
      toast({
        variant: "destructive",
        title: "Error loading course",
        description: error.message,
      });
      navigate("/");
    } else {
      setCourse(data);
      setEditedCourse(data);
      
      // Fetch workspace if course belongs to one
      if (data.workspace_id) {
        const { data: wsData } = await supabase
          .from("workspaces")
          .select("id, name, icon")
          .eq("id", data.workspace_id)
          .single();
        if (wsData) {
          setWorkspace(wsData);
        }
      }
    }
    setLoading(false);
  };

  const fetchSlideDecks = async () => {
    if (!id) return;
    
    const { data, error } = await supabase
      .from("generated_slide_decks")
      .select("*")
      .eq("project_id", id)
      .order("version", { ascending: false });

    if (!error && data) {
      setSlideDecks(data);
    }
  };

  const handleSave = async () => {
    if (!id) return;

    const objectivesForProject = editedObjectives
      .filter((o) => o.title.trim())
      .map((o) => ({
        title: o.title.trim(),
        ...(o.description?.trim() ? { description: o.description.trim() } : {}),
      }));

    // Save course details (objectives JSON stays in sync with course_objectives for older flows)
    const { error } = await supabase
      .from("learning_projects")
      .update({
        title: editedCourse.title,
        description: editedCourse.description,
        learner_profile: editedCourse.learner_profile,
        objectives: objectivesForProject,
        status: editedCourse.status,
      })
      .eq("id", id);

    if (error) {
      toast({
        variant: "destructive",
        title: "Error updating course",
        description: error.message,
      });
      return;
    }

    // Save course objectives
    await saveObjectives();

    toast({
      title: "Course updated",
      description: "Your changes have been saved successfully.",
    });
    setIsEditing(false);
    fetchCourse();
    fetchCourseObjectives();
  };

  const saveObjectives = async () => {
    if (!id) return;

    // Find objectives to delete (in original but not in edited)
    const editedIds = new Set(editedObjectives.filter(o => o.id && !o.id.startsWith('new-')).map(o => o.id));
    const toDelete = courseObjectives.filter(o => !editedIds.has(o.id));

    // Delete removed objectives
    for (const obj of toDelete) {
      await supabase.from("course_objectives").delete().eq("id", obj.id);
    }

    // Upsert objectives
    for (let i = 0; i < editedObjectives.length; i++) {
      const obj = editedObjectives[i];
      if (obj.id.startsWith('new-')) {
        // Insert new objective
        await supabase.from("course_objectives").insert({
          course_id: id,
          title: obj.title,
          description: obj.description,
          order_index: i,
        });
      } else {
        // Update existing objective
        await supabase.from("course_objectives").update({
          title: obj.title,
          description: obj.description,
          order_index: i,
        }).eq("id", obj.id);
      }
    }
  };

  const addObjective = () => {
    if (editedObjectives.length >= MAX_COURSE_OBJECTIVES) {
      toast({
        variant: "destructive",
        title: "Objective limit",
        description: `You can add at most ${MAX_COURSE_OBJECTIVES} objectives.`,
      });
      return;
    }
    const newObjective: CourseObjective = {
      id: `new-${Date.now()}`,
      title: "",
      description: "",
      order_index: editedObjectives.length,
    };
    setEditedObjectives([...editedObjectives, newObjective]);
  };

  const applyPastedObjectives = () => {
    const titles = splitPastedObjectiveLines(objectivesPasteDraft);
    if (titles.length === 0) {
      toast({
        variant: "destructive",
        title: "Nothing to add",
        description: "Paste at least one non-empty line.",
      });
      return;
    }
    const room = MAX_COURSE_OBJECTIVES - editedObjectives.length;
    if (room <= 0) {
      toast({
        variant: "destructive",
        title: "Objective limit",
        description: `You can have at most ${MAX_COURSE_OBJECTIVES} objectives.`,
      });
      return;
    }
    const take = titles.slice(0, room);
    const base = Date.now();
    const newOnes: CourseObjective[] = take.map((title, i) => ({
      id: `new-${base}-${i}`,
      title,
      description: "",
      order_index: editedObjectives.length + i,
    }));
    setEditedObjectives([...editedObjectives, ...newOnes]);
    setObjectivesPasteDraft("");
    toast({
      title: "Objectives added",
      description: `Added ${newOnes.length} objective${newOnes.length !== 1 ? "s" : ""} from paste.`,
    });
  };

  const updateObjective = (index: number, field: 'title' | 'description', value: string) => {
    const updated = [...editedObjectives];
    updated[index] = { ...updated[index], [field]: value };
    setEditedObjectives(updated);
  };

  const removeObjective = (index: number) => {
    setEditedObjectives(editedObjectives.filter((_, i) => i !== index));
  };

  if (!session || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground mb-2">Loading course...</p>
          <p className="text-sm text-muted-foreground">If this persists, the course may not exist.</p>
        </div>
      </div>
    );
  }

  // Free-tier "other" with one course: outline + download only, no lessons/skill tree/slides
  const isFreeTierLimitedView = userType === "other_one_course" && course.user_id === session?.user?.id;
  if (isFreeTierLimitedView) {
    const downloadOutline = () => {
      const parts: string[] = [`# ${course.title}\n`];
      if (freeTierAddieOutline) {
        const o = freeTierAddieOutline;
        if (o.analysis && typeof o.analysis === "object") parts.push("## Analysis\n", JSON.stringify(o.analysis, null, 2), "\n");
        if (o.design && typeof o.design === "object") parts.push("## Design\n", JSON.stringify(o.design, null, 2), "\n");
        if (o.development && typeof o.development === "object") parts.push("## Development\n", JSON.stringify(o.development, null, 2), "\n");
        if (o.implementation && typeof o.implementation === "object") parts.push("## Implementation\n", JSON.stringify(o.implementation, null, 2), "\n");
        if (o.evaluation && typeof o.evaluation === "object") parts.push("## Evaluation\n", JSON.stringify(o.evaluation, null, 2), "\n");
      } else {
        parts.push("No ADDIE outline generated for this course yet.");
      }
      const blob = new Blob([parts.join("")], { type: "text/plain;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(course.title || "course").replace(/[^a-zA-Z0-9-_]/g, "_")}-outline.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    };
    return (
      <div className="min-h-screen bg-background p-4 md:p-8">
        <div className="max-w-3xl mx-auto space-y-6">
          <Button variant="ghost" size="sm" onClick={() => navigate("/create")} className="gap-2 -ml-2">
            <ArrowLeft className="h-4 w-4" />
            Back to your course
          </Button>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                {course.title}
              </CardTitle>
              <CardDescription>
                You can view and download your course outline. Create an account to add lessons, slides, and more courses.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-2">
                <Button onClick={downloadOutline} className="gap-2">
                  <Download className="h-4 w-4" />
                  Download course outline
                </Button>
              </div>
              {freeTierAddieOutline ? (
                <div className="pt-4 border-t">
                  <AddieOutlineView outline={freeTierAddieOutline} />
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No ADDIE outline generated for this course yet.</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="border-b shrink-0">
        <div className="container mx-auto px-4 py-3 sm:py-4">
          <div className="flex items-center gap-2 mb-2">
            {workspace ? (
              <Button variant="ghost" size="sm" onClick={() => navigate(`/workspace/${workspace.id}`)} className="shrink-0">
                <ArrowLeft className="mr-1 sm:mr-2 h-4 w-4" />
                <span className="text-lg mr-1">{workspace.icon || "📁"}</span>
                <span className="truncate max-w-[140px] sm:max-w-none">{workspace.name}</span>
              </Button>
            ) : (
              <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")}>
                <ArrowLeft className="mr-1 sm:mr-2 h-4 w-4" />
                Back to Dashboard
              </Button>
            )}
          </div>
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
            <div className="flex-1 min-w-0">
              {isEditing ? (
                <Input
                  value={editedCourse.title || ""}
                  onChange={(e) =>
                    setEditedCourse({ ...editedCourse, title: e.target.value })
                  }
                  className="text-xl sm:text-3xl font-bold mb-2"
                />
              ) : (
                <h1 className="text-xl sm:text-3xl font-bold break-words">{course.title}</h1>
              )}
              {isEditing ? (
                <Textarea
                  value={editedCourse.description || ""}
                  onChange={(e) =>
                    setEditedCourse({ ...editedCourse, description: e.target.value })
                  }
                  className="text-muted-foreground mt-2"
                  rows={2}
                />
              ) : (
                <p className="text-muted-foreground text-sm sm:text-base">
                  {course.description || "No description"}
                </p>
              )}
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
              <SyllabusPrintView courseId={id!} courseTitle={course.title} />
              <Button variant="outline" size="sm" onClick={() => navigate(`/instructor/${id}`)}>
                <BarChart3 className="mr-1 sm:mr-2 h-4 w-4" />
                <span className="hidden sm:inline">Instructor dashboard</span>
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowManageMembers(true)}>
                <Users className="mr-1 sm:mr-2 h-4 w-4" />
                {course?.is_public ? "Shared (public)" : "Share"}
              </Button>
              <Button variant="outline" size="sm" onClick={() => navigate(`/learn/courses/${id}?preview=designer`)} data-tutorial="preview-learner">
                <GraduationCap className="mr-1 sm:mr-2 h-4 w-4" />
                <span className="hidden md:inline">Preview as Learner</span>
              </Button>
              {isEditing ? (
                <>
                  <Button size="sm" onClick={handleSave}>Save Changes</Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setIsEditing(false);
                      setEditedCourse(course);
                      setEditedObjectives(courseObjectives);
                    }}
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                <Button size="sm" onClick={() => setIsEditing(true)}>Edit Course</Button>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="flex flex-1 min-h-0">
        <aside className="w-56 shrink-0 border-r bg-muted/30 flex flex-col">
          <ScrollArea className="flex-1 py-3">
            <nav className="grid gap-0.5 px-2">
              <Button variant={activeSection === "outline" ? "secondary" : "ghost"} className="justify-start gap-2 font-medium" onClick={() => setActiveSection("outline")}>
                <FileText className="h-4 w-4 shrink-0" />
                Course Outline
              </Button>
              {activeSection === "outline" && id && (
                <CourseOutlineSidebar
                  courseId={id}
                  creationOutline={addieOutline?.creation_outline ?? null}
                  onTreeUpdated={() => {
                    fetchAddieOutline();
                    window.dispatchEvent(new CustomEvent("course-content-updated"));
                  }}
                />
              )}
              <Button variant={activeSection === "materials" ? "secondary" : "ghost"} className="justify-start gap-2" onClick={() => setActiveSection("materials")}>
                <FileStack className="h-4 w-4 shrink-0" />
                Materials
              </Button>
              <Button variant={activeSection === "skilltree" ? "secondary" : "ghost"} className="justify-start gap-2" onClick={() => setActiveSection("skilltree")} data-tutorial="skill-tree-tab">
                <Network className="h-4 w-4 shrink-0" />
                Skill Tree
              </Button>
              <Button variant={activeSection === "slides" ? "secondary" : "ghost"} className="justify-start gap-2" onClick={() => setActiveSection("slides")}>
                <Presentation className="h-4 w-4 shrink-0" />
                Slides
              </Button>
              <Button variant={activeSection === "details" ? "secondary" : "ghost"} className="justify-start gap-2" onClick={() => setActiveSection("details")}>
                <Settings className="h-4 w-4 shrink-0" />
                Course Details
              </Button>
            </nav>
          </ScrollArea>
        </aside>
        <div className="flex-1 min-w-0 overflow-auto">
          <div className="container max-w-4xl mx-auto px-4 py-6">
            {activeSection === "outline" && (
              <div className="space-y-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2 className="text-xl font-semibold mb-1">Course Outline</h2>
                    <p className="text-sm text-muted-foreground">
                      {addieOutline?.creation_outline
                        ? "Outline from course creation"
                        : "ADDIE instructional design framework — the main artifact for this course"}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                  {id && <SyllabusPrintView courseId={id} courseTitle={course.title} />}
                  {(addieOutline?.creation_outline || addieOutline) && id && (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={generatingOutline}
                      onClick={async () => {
                        if (!id) return;
                        setGeneratingOutline(true);
                        try {
                          if (addieOutline?.creation_outline) {
                            const { error } = await invokeFunction("refresh-outline-from-materials", { body: { projectId: id } });
                            if (error) throw new Error((error as { message?: string; error?: string }).message || (error as { message?: string; error?: string }).error || "Failed to refresh");
                            toast({ title: "Outline refreshed", description: "Course outline updated from recently uploaded materials." });
                          } else {
                            const { error } = await supabase.functions.invoke("generate-addie", { body: { projectId: id } });
                            if (error) throw error;
                            toast({ title: "Outline refreshed", description: "ADDIE outline updated from materials." });
                          }
                          await fetchAddieOutline();
                          window.dispatchEvent(new CustomEvent("course-content-updated"));
                          // Fire-and-forget: gather research sources into Materials tab
                          if (course?.title) {
                            invokeFunction("deep-research", { body: { topic: course.title, courseTitle: course.title, projectId: id, workspaceId: course.workspace_id || null } })
                              .then(() => window.dispatchEvent(new CustomEvent("materials-updated")))
                              .catch(() => { /* non-fatal */ });
                          }
                        } catch (e: unknown) {
                          const err = e as { error?: string; message?: string };
                          const msg = err?.error || (e instanceof Error ? e.message : String(e)) || "Failed to refresh outline";
                          toast({ variant: "destructive", title: "Error", description: msg });
                        } finally {
                          setGeneratingOutline(false);
                        }
                      }}
                    >
                      <RefreshCw className={`h-4 w-4 mr-2 ${generatingOutline ? "animate-spin" : ""}`} />
                      {generatingOutline ? "Refreshing…" : "Refresh with materials"}
                    </Button>
                  )}
                  </div>
                </div>
                {addieOutline?.creation_outline ? (
                  <CreationOutlineView
                    outline={addieOutline.creation_outline}
                    courseId={id}
                    courseTitle={course?.title}
                    onOutlineUpdated={() => {
                      fetchAddieOutline();
                      window.dispatchEvent(new CustomEvent("course-content-updated"));
                    }}
                  />
                ) : addieOutline ? (
                  <AddieOutlineView outline={addieOutline} />
                ) : (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <FileText className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                      <p className="text-muted-foreground mb-2">No course outline yet</p>
                      <p className="text-sm text-muted-foreground mb-4">Generate an ADDIE outline from your course details and materials to use as the main artifact and to create the skill tree.</p>
                      <Button
                        onClick={async () => {
                          if (!id) return;
                          setGeneratingOutline(true);
                          try {
                            const { error } = await supabase.functions.invoke("generate-addie", { body: { projectId: id } });
                            if (error) throw error;
                            toast({ title: "Outline generated", description: "Your course outline is ready." });
                            await fetchAddieOutline();
                            // Fire-and-forget: gather research sources into Materials tab
                            if (course?.title) {
                              invokeFunction("deep-research", { body: { topic: course.title, courseTitle: course.title, projectId: id, workspaceId: course.workspace_id || null } })
                                .then(() => window.dispatchEvent(new CustomEvent("materials-updated")))
                                .catch(() => { /* non-fatal */ });
                            }
                          } catch (e) {
                            toast({ variant: "destructive", title: "Error", description: e instanceof Error ? e.message : "Failed to generate outline" });
                          } finally {
                            setGeneratingOutline(false);
                          }
                        }}
                        disabled={generatingOutline}
                      >
                        <Sparkles className="mr-2 h-4 w-4" />
                        {generatingOutline ? "Generating..." : "Generate course outline"}
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
            <div
              style={{ display: activeSection === "skilltree" ? "block" : "none" }}
              className="space-y-4"
              aria-hidden={activeSection !== "skilltree"}
            >
              <SkillTreeView courseId={id!} addieOutline={addieOutline} onOutlineRefetch={fetchAddieOutline} />
            </div>
            {activeSection === "materials" && (
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Course Materials</CardTitle>
                    <CardDescription>
                      Upload and manage course content files
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={async () => {
                        if (!course?.title || !id) return;
                        setAddingSourcesWithAI(true);
                        try {
                          const { data, error } = await invokeFunction("deep-research", {
                            body: {
                              topic: course.title,
                              courseTitle: course.title,
                              projectId: id,
                              workspaceId: course.workspace_id || null,
                            },
                          });
                          if (error) throw new Error(error.message || error.error || "Failed to gather sources");
                          const count = data?.count ?? data?.materials?.length ?? 0;
                          toast({ title: "Sources added", description: `Found and added ${count} verified sources to your course materials.` });
                          window.dispatchEvent(new CustomEvent("materials-updated"));
                        } catch (err) {
                          toast({ variant: "destructive", title: "Error", description: err instanceof Error ? err.message : "Failed to add sources" });
                        } finally {
                          setAddingSourcesWithAI(false);
                        }
                      }}
                      disabled={addingSourcesWithAI || !course?.title}
                    >
                      <Sparkles className={`mr-2 h-4 w-4 ${addingSourcesWithAI ? "animate-pulse" : ""}`} />
                      {addingSourcesWithAI ? "Finding sources..." : "Add sources with AI"}
                    </Button>
                    <Button onClick={() => setUploadDialogOpen(true)}>
                      <Upload className="mr-2 h-4 w-4" />
                      Upload Material
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <MaterialsList projectId={id!} />
              </CardContent>
            </Card>
          </div>
            )}
            {activeSection === "slides" && (
              <div className="space-y-4">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>PowerPoint Slide Decks</CardTitle>
                        <CardDescription>AI-generated presentations from course materials</CardDescription>
                      </div>
                      <Button onClick={() => setSlidesDialogOpen(true)}>
                        <Presentation className="mr-2 h-4 w-4" />
                        Generate Slides
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <SlideDecksView decks={slideDecks} onUpdate={fetchSlideDecks} />
                  </CardContent>
                </Card>
              </div>
            )}
            {activeSection === "details" && (
              <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Course Information</CardTitle>
                <CardDescription>
                  View and edit course metadata
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h3 className="font-semibold mb-2">Status</h3>
                  {isEditing ? (
                    <select
                      value={editedCourse.status || "draft"}
                      onChange={(e) =>
                        setEditedCourse({ ...editedCourse, status: e.target.value })
                      }
                      className="w-full p-2 border rounded"
                    >
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                      <option value="archived">Archived</option>
                    </select>
                  ) : (
                    <Badge>{course.status}</Badge>
                  )}
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Learner Profile</h3>
                  {isEditing ? (
                    <Textarea
                      value={JSON.stringify(editedCourse.learner_profile || {}, null, 2)}
                      onChange={(e) => {
                        try {
                          setEditedCourse({
                            ...editedCourse,
                            learner_profile: JSON.parse(e.target.value),
                          });
                        } catch (err) {
                          // Invalid JSON, ignore
                        }
                      }}
                      rows={6}
                      className="font-mono text-sm"
                    />
                  ) : (
                    <pre className="bg-muted p-4 rounded text-sm overflow-auto">
                      {JSON.stringify(course.learner_profile, null, 2) || "Not set"}
                    </pre>
                  )}
                </div>

                {/* Course objectives: designer-authored; inform skill tree + lessons */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold flex items-center gap-2">
                      <Target className="h-4 w-4 text-primary" />
                      Learning objectives
                    </h3>
                    {isEditing && (
                      <Button variant="outline" size="sm" onClick={addObjective}>
                        <Plus className="h-4 w-4 mr-1" />
                        Add row
                      </Button>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">
                    {isEditing
                      ? "Paste a list from a syllabus or rubric, or edit rows below. Save the course before generating the skill tree or lessons so AI can use these objectives."
                      : "These objectives guide skill tree generation, lesson content, and exams. Edit them in Course Details when the course is in edit mode."}
                  </p>
                  {isEditing ? (
                    <div className="space-y-3">
                      <div className="space-y-2 rounded-lg border border-dashed bg-muted/20 p-3">
                        <Label htmlFor="objectives-paste">Paste objectives (one per line)</Label>
                        <Textarea
                          id="objectives-paste"
                          rows={5}
                          placeholder={
                            "Example:\nExplain the core principles of…\nApply X when diagnosing Y\nCompare approaches A and B"
                          }
                          value={objectivesPasteDraft}
                          onChange={(e) => setObjectivesPasteDraft(e.target.value)}
                          className="font-mono text-sm"
                        />
                        <div className="flex flex-wrap gap-2">
                          <Button type="button" variant="secondary" size="sm" onClick={applyPastedObjectives}>
                            Add pasted lines
                          </Button>
                          <Button type="button" variant="ghost" size="sm" onClick={() => setObjectivesPasteDraft("")}>
                            Clear box
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Leading bullets (•, -, *) and numbers are removed. Up to {MAX_COURSE_OBJECTIVES} objectives total.
                        </p>
                      </div>
                      {editedObjectives.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No objectives yet. Paste a list or add rows.</p>
                      ) : (
                        editedObjectives.map((obj, index) => (
                          <div key={obj.id} className="flex gap-3 p-3 rounded-lg border bg-muted/30">
                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold text-sm">
                              {index + 1}
                            </div>
                            <div className="flex-1 space-y-2">
                              <Input
                                placeholder="Objective title"
                                value={obj.title}
                                onChange={(e) => updateObjective(index, 'title', e.target.value)}
                              />
                              <Textarea
                                placeholder="Description (optional)"
                                value={obj.description || ""}
                                onChange={(e) => updateObjective(index, 'description', e.target.value)}
                                rows={2}
                              />
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-destructive hover:text-destructive"
                              onClick={() => removeObjective(index)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))
                      )}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {courseObjectives.length === 0 ? (
                        <p className="text-sm text-muted-foreground">
                          No objectives yet. Add them here (paste or rows), save, then generate or regenerate the skill tree.
                        </p>
                      ) : (
                        courseObjectives.map((obj, index) => (
                          <div key={obj.id} className="flex gap-3 p-3 rounded-lg bg-muted/30">
                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold text-sm">
                              {index + 1}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium">{obj.title}</p>
                              {obj.description && (
                                <p className="text-sm text-muted-foreground mt-1">{obj.description}</p>
                              )}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Created</h3>
                  <p className="text-sm text-muted-foreground">
                    {new Date(course.created_at).toLocaleDateString()}
                  </p>
                </div>

                {/* Certificate Alignment */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold flex items-center gap-2">
                      <Award className="h-4 w-4 text-yellow-500" />
                      Certificate Alignment
                    </h3>
                  </div>
                  {loadingCertifications ? (
                    <p className="text-sm text-muted-foreground">Loading certifications...</p>
                  ) : !Array.isArray(certifications) || !certifications || certifications.length === 0 ? (
                    <div className="text-center py-8 border rounded-lg bg-muted/30">
                      <Award className="mx-auto h-8 w-8 text-muted-foreground mb-2" />
                      <p className="text-sm text-muted-foreground">
                        No certifications aligned yet
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Generate a skill tree to automatically align certifications
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <Badge variant="secondary">
                        {certifications.length} {certifications.length === 1 ? 'Certification' : 'Certifications'}
                      </Badge>
                      <div className="grid gap-3 md:grid-cols-2">
                        {certifications.map((cert, i) => (
                          <div key={i} className="flex items-start gap-3 p-3 rounded-lg border bg-muted/30">
                            <Award className="h-5 w-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium truncate">{typeof cert === 'object' && cert ? (cert.name || 'Unnamed') : String(cert || 'Unknown')}</h4>
                              <p className="text-sm text-muted-foreground mt-1">
                                {typeof cert === 'object' && cert ? (cert.issuer || 'Unknown issuer') : 'Unknown issuer'}
                              </p>
                              {typeof cert === 'object' && cert && cert.url && (
                                <a 
                                  href={cert.url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-xs text-primary flex items-center gap-1 mt-2 hover:underline"
                                >
                                  Learn more <ExternalLink className="h-3 w-3" />
                                </a>
                              )}
                              {typeof cert === 'object' && cert && cert.domains && Array.isArray(cert.domains) && cert.domains.length > 0 && (
                                <div className="mt-2 space-y-1">
                                  <p className="text-xs font-semibold text-muted-foreground">Domains:</p>
                                  <ul className="text-xs text-muted-foreground space-y-1">
                                    {cert.domains.map((domain: string, idx: number) => (
                                      <li key={idx}>• {domain}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
              </div>
            )}
          </div>
        </div>
      </main>

        <UploadMaterialDialog
          open={uploadDialogOpen}
          onOpenChange={setUploadDialogOpen}
          projectId={id!}
        />

        <GenerateSlidesDialog
          open={slidesDialogOpen}
          onOpenChange={setSlidesDialogOpen}
          projectId={id!}
          onSuccess={fetchSlideDecks}
        />

        <ShareCourseDialog
          open={showManageMembers}
          onOpenChange={setShowManageMembers}
          courseId={course?.id || ""}
          courseName={course?.title || ""}
          isOwner={course?.user_id === session?.user?.id}
          initialIsPublic={course?.is_public ?? false}
          onPublicChanged={(val) => setCourse((c) => c ? { ...c, is_public: val } : c)}
        />
    </div>
  );
};

export default Course;
